// Guided-tour step definitions (v2).
//
// Steps bind to EXISTING `data-testid` anchors (mostly the always-present client
// sidebar nav, so anchors stay valid as the tour navigates page-to-page).
//
// Each step declares:
//   - element:     CSS selector for the anchor
//   - route:       client route to navigate to when the step activates (optional)
//   - title/description
//   - side/align:  driver.js popover placement
//   - requires:    { role?: [...], capability?: 'can_...' } — role/permission gate
//   - since:       tour version the step was introduced (drives "What's new")

export const TOUR_VERSION = 2;

const STEPS = [
  {
    id: 'dashboard',
    element: '[data-testid="nav-dashboard"]',
    route: '/client/dashboard',
    title: 'Your Dashboard',
    description:
      'This is your home base — see your next delivery, account stats, open balance, and quick reorder all in one place.',
    side: 'right',
    align: 'start',
    since: 1,
  },
  {
    id: 'deadline',
    element: '[data-testid="next-delivery-card"]',
    route: '/client/dashboard',
    title: 'Order-by deadline',
    description:
      'Keep an eye on this banner: it shows your next delivery date and the latest time to order for it, based on lead time and your timezone.',
    side: 'bottom',
    align: 'start',
    since: 1,
  },
  {
    id: 'shop',
    element: '[data-testid="nav-shop"]',
    route: '/client/shop',
    title: 'Shop by delivery date',
    description:
      'Pick a delivery date first, then browse the catalog available for that day. Prices already reflect your tier — that\'s "your price".',
    side: 'right',
    align: 'start',
    since: 1,
  },
  {
    id: 'orders',
    element: '[data-testid="nav-orders"]',
    route: '/client/orders',
    title: 'Track & manage orders',
    description:
      'View every order and its status. Edit before the cutoff, cancel when allowed, and reorder favorites in one click.',
    side: 'right',
    align: 'start',
    since: 1,
  },
  {
    id: 'invoices',
    element: '[data-testid="nav-invoices"]',
    route: '/client/invoices',
    title: 'Invoices, PDFs & adjustments',
    description:
      'Invoices are created on delivery. View details, download a PDF, and request a Guaranteed Returns adjustment when eligible.',
    side: 'right',
    align: 'start',
    since: 1,
  },
  {
    id: 'payments',
    element: '[data-testid="nav-payment methods"]',
    route: '/client/payment-methods',
    title: 'Payments & auto-pay',
    description:
      'Save a card or bank account (ACH), pay invoices instantly, set a default, and enable auto-pay so invoices pay themselves.',
    side: 'right',
    align: 'start',
    requires: { capability: 'can_pay_invoices' },
    since: 1,
  },
  {
    id: 'messages',
    element: '[data-testid="nav-messages"]',
    route: '/client/messages',
    title: 'Messages',
    description: 'Read updates and announcements from the MAD Donuts team here. Click any message for the full details.',
    side: 'right',
    align: 'start',
    since: 1,
  },
  {
    id: 'reports',
    element: '[data-testid="nav-reports"]',
    route: '/client/reports',
    title: 'Reports',
    description: 'See your spending history, most-ordered products, and ordering trends over time.',
    side: 'right',
    align: 'start',
    requires: { capability: 'can_view_reports' },
    since: 1,
  },
  {
    id: 'profile',
    element: '[data-testid="nav-profile"]',
    route: '/client/profile',
    title: 'Profile, locations & preferences',
    description:
      'Manage company info and contacts, add delivery locations, set notification preferences, and change your password.',
    side: 'right',
    align: 'start',
    requires: { capability: 'can_edit_profile' },
    since: 1,
  },
  {
    id: 'team',
    element: '[data-testid="nav-team"]',
    route: '/client/team',
    title: 'NEW: Team & multiple logins',
    description:
      'Your account can have many logins with roles — Owner, Manager, and Member. Owners and Managers can invite and manage teammates here.',
    side: 'right',
    align: 'start',
    requires: { role: ['owner', 'manager'] },
    since: 2,
  },
  {
    id: 'help',
    element: '[data-testid="help-tour-btn"]',
    title: 'NEW: Help Center & replay anytime',
    description:
      'Open Help & Tour anytime to search step-by-step articles, get help for the current page, or replay this tour. You\'re all set!',
    side: 'bottom',
    align: 'end',
    since: 2,
  },
];

// Permissive defaults so a failed permissions fetch never hides core steps from an owner.
const DEFAULT_PERMS = {
  can_place_orders: true,
  can_view_invoices: true,
  can_pay_invoices: true,
  can_edit_profile: true,
  can_manage_users: true,
  can_view_reports: true,
};

export function buildTourSteps({ mode, seenVersion = 0, permissions } = {}) {
  const role = permissions?.role || 'owner';
  const perms = permissions?.permissions || DEFAULT_PERMS;

  return STEPS.filter((s) => {
    if (mode === 'whats_new' && !(s.since > seenVersion)) return false;
    if (s.requires?.role && !s.requires.role.includes(role)) return false;
    if (s.requires?.capability && !perms[s.requires.capability]) return false;
    return true;
  });
}
